import { maybe, rd } from '@passionware/monads';
import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { throttle } from 'lodash';
import { ShortlistBlock_normalized } from 'v4/entities/associations/resource__shortlist/resource__shortlist.schemas';
import { V1Action } from '../../../../v4/migration/actionAsEventMiddleware';
import { ShortlistApi } from '../../../api/shortlist/shortlist.api';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { WithServices } from '../../../platform/ts/services';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { ShortlistService } from './ShortlistService';

export function createShortlistService(
  config: WithServices<[WithMessageService, WithErrorService]> & {
    api: ShortlistApi;
    queryClient: QueryClient;
    v1Event: SimpleReadOnlyEvent<V1Action>;
  }
): ShortlistService {
  const {
    api,
    queryClient,
    v1Event,
    services: { messageService, errorService }
  } = config;
  const interceptor = errorService.withCategory('ShortlistService');

  const invalidateAll = throttle(
    async () => {
      queryClient.removeQueries({
        queryKey: ['shortlist'],
        type: 'inactive'
      });
      await queryClient.invalidateQueries({
        queryKey: ['shortlist'],
        type: 'active'
      });
    },
    // we don't want to invalidate the cache too often
    3000,
    {
      // after period of inactivity, we want to invalidate the cache immediately
      leading: true,
      // we want to make sure that last invalidation is executed
      trailing: true
    }
  );

  v1Event.addListener(async event => {
    switch (event.type) {
      case 'UPDATE_SHORTLIST_SUCCESS':
      case 'DELETE_SHORTLIST_SUCCESS':
      case 'CREATE_SHORTLIST_SUCCESS':
      case 'SHORTLIST_UPDATED':
      case 'UPDATE_SHORTLIST_BLOCK_SUCCESS':
      case 'COMMENT_SHORTLIST_BLOCK_SUCCESS':
      case 'CREATE_SHORTLIST_BLOCK_SUCCESS':
        await invalidateAll();
        return;
    }
  });

  messageService.reportSystemEffect.subscribeToRequest(async message => {
    for (const effect of message.request.scope) {
      switch (effect.entity) {
        case 'shortlist': {
          // first, we invalidate all list queries
          queryClient.removeQueries({
            queryKey: ['shortlist', 'list'],
            type: 'inactive'
          });
          await queryClient.invalidateQueries({
            queryKey: ['shortlist', 'list'],
            type: 'active'
          });
          // then we invalidate the detail query
          await queryClient.invalidateQueries({
            queryKey: ['shortlist', 'detail', effect.entityId]
          });
        }
      }
    }
    message.sendResponse();
  });

  const service: ShortlistService = {
    useShortlists: query =>
      ensureIdleQuery(
        query,
        useQuery(
          {
            enabled: maybe.isPresent(query),
            queryKey: ['shortlist', 'list', query],
            queryFn: interceptor.wrap(async () => {
              const result = await maybe.map(query, api.getShortlists);
              // Populate detail cache with shortlists from list response
              // we cannot do this - list query does not contain all required details
              // if (maybe.isPresent(result)) {
              //   for (const shortlist of result.items) {
              //     queryClient.setQueryData(
              //       ['shortlist', 'detail', shortlist.id],
              //       shortlist
              //     );
              //   }
              // }
              return result;
            })
          },
          queryClient
        )
      ),
    ensureShortlists: query =>
      queryClient.fetchQuery({
        queryKey: ['shortlist', 'list', query],
        queryFn: interceptor.wrap(() => api.getShortlists(query))
      }),
    useShortlist: id =>
      ensureIdleQuery(
        id,
        useQuery(
          {
            enabled: maybe.isPresent(id),
            queryKey: ['shortlist', 'detail', id],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              if (!id) {
                throw new Error('Shortlist ID is required');
              }
              return await interceptor.wrap(() => api.getShortlist(id))();
            }
          },
          queryClient
        )
      ),
    ensureShortlist: id =>
      queryClient.fetchQuery({
        queryKey: ['shortlist', 'detail', id],
        queryFn: interceptor.wrap(() => api.getShortlist(id))
      }),
    useShortlistBlocks(query) {
      const shortlist = service.useShortlist(
        maybe.map(query, q => q.shortlistId)
      );
      const matchingResourceIds = ensureIdleQuery(
        query,
        useQuery({
          queryKey: [
            'shortlist',
            'detail',
            query?.shortlistId,
            'blocks',
            query
          ],
          enabled: maybe.isPresent(query),
          queryFn: interceptor.wrap(() => api.searchShortlistResources(query!))
        })
      );

      const combined = rd.useStable(
        rd.combine({ shortlist, matchingResourceIds })
      );

      return rd.useMemoMap(combined, ({ shortlist, matchingResourceIds }) => {
        return {
          items: matchingResourceIds.items.map(
            id =>
              maybe.getOrThrow(
                shortlist.shortlist_blocks.find(s => s.contact_id === id),
                `Shortlist block with contact id ${id} not found in shortlist ${shortlist.id} cache`
              ) as ShortlistBlock_normalized
          ),
          count: matchingResourceIds.count
        };
      });
    }
  };
  return service;
}
